/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Vehicle } from 'rest-client';

import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';
import {
  CarMake,
  CarMakesResponse,
  CarModel,
  CarModelsResponse,
  CarPropulsion,
  CarPropulsionsResponse,
  UmcData,
  UmcPayload,
  VehicleReturned,
} from '@app/modules/shared/models';
import { GoogleTagManagerService } from '@core/services/analytics/google-tag-manager.service';

import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

interface ExtendedCarModel extends CarModel {
  friendlyName?: string;
  year?: string;
}

/**
 * > Creates new vehicle by make(brand), model, propulsión (UMC).
 *
 * > Shows Make-Model-Propulsion selectors when selected vehicle at ***bkg-vehicle-dialog-select-client-car***
 * haven't UMC.
 *
 * > Shows Make-Model-Propulsion selectors when created Vehicle at ***bkg-vehicle-dialog-license-plate***
 * fails at auto-identify and needs UMC.
 *
 */

@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog-umc-selectors',
  templateUrl: './vehicle-dialog-umc-selectors.component.html',
})
export class VehicleDialogUmcSelectorsComponent implements OnInit, OnChanges {
  @Output() errorReturn = new EventEmitter<HttpErrorResponse>();
  @Output() umcSelectorsReturn = new EventEmitter<VehicleReturned>();
  @Output() notFoundCarOptionReturn = new EventEmitter<boolean>();
  @Input() vehicleId: number;
  @Input() plateNotFounded: string;
  @Input() notFoundedVehicleID: number;
  @Input() isLogged: boolean;
  @Input() createNewVehicle: boolean;
  @Input() addMoreInfoUmc: boolean;
  @Input() clientId: number;
  @Input() modifiedVehicle: Vehicle;

  makes: CarMake[];
  models: ExtendedCarModel[];
  filterModels: ExtendedCarModel[];
  modelYears: ExtendedCarModel[];
  propulsions: CarPropulsion[];

  umcForm: FormGroup;
  vehicleDataForm: FormGroup;
  umcSelectorValues: UmcData[];

  loadingModels: boolean;
  loadingPropulsion: boolean;

  constructor(
    private vehicleService: VehicleService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    if (!this.createNewVehicle) {
      this.getCarMakes();
      this.buildUmcForm();
      this.subscribeToMakeChanges();
      this.subscribeToModelChanges();
      this.subscribeToYearChanges();
    } else {
      this.buildvehicleDataForm();
    }
  }

  ngOnChanges(): void {
    if (this.addMoreInfoUmc) {
      this.getControl('makeIdSelect').disable();
      this.getControl('modelIdSelect').disable();
      this.getControl('modelYearIdSelect').disable();
      this.getControl('propulsionSelect').disable();
    }
  }

  get showInBody(): string {
    if (document.body.offsetWidth < 1000) {
      return '';
    }
    return 'body';
  }

  private buildUmcForm = (): void => {
    this.umcForm = this.formBuilder.group({
      makeIdSelect: [null, [Validators.required]],
      modelIdSelect: [{ value: null, disabled: true }, [Validators.required]],
      modelYearIdSelect: [null, null],
      propulsionSelect: [
        { value: null, disabled: true },
        [Validators.required],
      ],
    });
  };

  private buildvehicleDataForm = (): void => {
    this.vehicleDataForm = this.formBuilder.group({
      brand: [null, [Validators.required]],
      model: [null, [Validators.required]],
      color: null,
    });
  };

  private subscribeToMakeChanges(): void {
    const control = this.getControl('makeIdSelect');
    control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((makeId: number) => this.onSuccessMakeValueChanges(makeId));
  }

  private subscribeToModelChanges(): void {
    const control = this.getControl('modelIdSelect');
    control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((model: string) => this.onSuccessModelValueChanges(model));
  }

  private subscribeToYearChanges(): void {
    const control = this.getControl('modelYearIdSelect');
    control.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((year: string) => this.onSuccessYearValueChanges(year));
  }

  private onSuccessMakeValueChanges(makeId: number): void {
    if (makeId && !this.addMoreInfoUmc) {
      if (this.getControlValue('modelIdSelect')) {
        this.resetControl('modelIdSelect');
        this.resetControl('propulsionSelect');
      }
      this.modelYears = null;
      this.getCarModels(makeId);
    }
  }

  private onSuccessYearValueChanges(carId: string): void {
    if (carId && !this.addMoreInfoUmc) {
      const modelId = this.models.find(
        (filterModel: ExtendedCarModel) => filterModel.id === carId
      ).id;
      this.resetControl('propulsionSelect');
      this.getCarPropulsions(Number(modelId));
    }
  }

  private onSuccessModelValueChanges(modelId: string): void {
    if (!this.addMoreInfoUmc) {
      if (this.getControlValue('modelYearIdSelect')) {
        const yearControl = this.getControl('modelYearIdSelect');
        yearControl.reset(null, { emitEvent: false });
        yearControl.updateValueAndValidity();
        this.resetControl('propulsionSelect');
      }
      if (this.getControlValue('propulsionSelect'))
        this.resetControl('propulsionSelect');
      this.modelYears = null;
      const model = Number(modelId);
      if (model > 0) {
        const selectedModel = this.filterModels?.find(
          (filterModel: ExtendedCarModel) => filterModel.id === modelId
        );
        const sameModels = this.models?.filter(
          (filterModel: ExtendedCarModel) =>
            filterModel.friendlyName === selectedModel.friendlyName
        );
        if (sameModels.length > 1) {
          const yearControl = this.getControl('modelYearIdSelect');
          yearControl.setValidators(Validators.required);
          yearControl.updateValueAndValidity();
          this.modelYears = this.reorderYears(sameModels);
        } else {
          const yearControl = this.getControl('modelYearIdSelect');
          yearControl.clearValidators();
          yearControl.updateValueAndValidity();
        }
        this.resetControl('propulsionSelect');
        this.getCarPropulsions(Number(modelId));
      }
    }
  }

  private reorderYears(models: ExtendedCarModel[]): ExtendedCarModel[] {
    const regex = /\D*(?:\d\D*){4}$/;
    const reorderedModels: ExtendedCarModel[] = [];
    let lastYearModel;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let prevSort: any[] = [];

    models.forEach((model: ExtendedCarModel, i: number) => {
      if (!regex.exec(model.year)) {
        lastYearModel = model;
      } else {
        const obj = { sliceYear: model.year.slice(-2), index: i };
        prevSort.push(obj);
      }
    });

    prevSort = prevSort.sort((a: any, b: any) => {
      return a['sliceYear'] - b['sliceYear'];
    });

    prevSort.forEach((m) => {
      reorderedModels.push(models[m.index]);
    });

    if (lastYearModel) reorderedModels.push(lastYearModel);

    return reorderedModels;
  }

  private resetControl = (controlName: string): void => {
    const control = this.getControl(controlName);
    control.reset(null, { emitEvent: false });
    control.updateValueAndValidity();
    control.disable();
  };

  private enableControl = (controlName: string): void => {
    this.umcForm.controls[controlName].enable();
  };

  private getCarMakes = (): void => {
    this.vehicleService
      .getCarMakes()
      .pipe(untilDestroyed(this))
      .subscribe(
        ({ car_makes }: CarMakesResponse) => {
          this.makes = car_makes;
          this.enableControl('makeIdSelect');
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private getCarModels = (makeId: number): void => {
    this.loadingModels = true;
    this.vehicleService
      .getCarModels(makeId)
      .pipe(untilDestroyed(this))
      .subscribe(
        ({ car_models }: CarModelsResponse) => {
          this.models = car_models;

          this.models.forEach((model: ExtendedCarModel) => {
            const regex = /(.*)\(([^\(\)]+)\)?(.*)?\(([\d-]+)\)?/gm;
            let uglyYear = undefined;
            let m;
            model.friendlyName = model.model_description;
            model.year = '-';

            while ((m = regex.exec(model.model_description)) !== null) {
              if (m && m[1] !== undefined) {
                model.friendlyName = m[1].trim();
                model.friendlyName =
                  m[3] !== undefined
                    ? model.friendlyName + ' ' + m[3].trim()
                    : model.friendlyName;
                uglyYear = m[4];
              }
            }
            if (uglyYear !== undefined) {
              const splitYear = uglyYear.split('-');
              let returnedYear = '';
              if (splitYear[1] === '') {
                returnedYear = this.translateService.instant(
                  'sharedModule.bkgVehicleDialogUmcSelectors.fromToCurrent',
                  { from: splitYear[0] }
                ) as string;
              } else {
                returnedYear = this.translateService.instant(
                  'sharedModule.bkgVehicleDialogUmcSelectors.fromToYear',
                  { from: splitYear[0], to: splitYear[1] }
                ) as string;
              }
              model.year = returnedYear;
            } else {
              model.year = '--';
            }
          });
          this.filterModels = this.models?.filter(
            (v, i, a) =>
              a.findIndex((t) => t.friendlyName === v.friendlyName) === i
          );
          this.getControl('modelIdSelect').setErrors(null);
          this.enableControl('modelIdSelect');
          this.loadingModels = false;
        },
        (error: HttpErrorResponse) => {
          this.loadingModels = false;
          this.errorReturn.emit(error);
        }
      );
  };

  private getCarPropulsions = (modelId: number): void => {
    this.loadingPropulsion = true;
    this.vehicleService
      .getCarPropulsions(modelId)
      .pipe(untilDestroyed(this))
      .subscribe(
        ({ propulsion_codes }: CarPropulsionsResponse) => {
          this.propulsions = propulsion_codes;
          this.enableControl('propulsionSelect');
          this.loadingPropulsion = false;
        },
        (error: HttpErrorResponse) => {
          this.loadingPropulsion = false;
          this.errorReturn.emit(error);
        }
      );
  };

  sendUmcForm(): void {
    const umcPayload = this.createPayload();
    if (this.isLogged && this.vehicleId) {
      this.setVehicleUmc(this.vehicleId, umcPayload);
    } else if (this.notFoundedVehicleID) {
      this.setVehicleUmc(this.notFoundedVehicleID, umcPayload);
    } else {
      this.createNewVehicleWithUmc(umcPayload);
    }
  }

  sendVehicleDataForm(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    const payload: any = {};
    const brand = this.vehicleDataForm.get('brand').value as string;
    const model = this.vehicleDataForm.get('model').value as string;
    const color = this.vehicleDataForm.get('color').value as string;
    const vehicleDescription = brand + ' ' + model + ' ' + color;
    payload.type = vehicleDescription;
    payload.plate = this.plateNotFounded;
    if (this.clientId) {
      payload.client_id = this.clientId;
    }
    this.vehicleService
      .createVehicle(payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.googleTagManagerService.pushOnDataLayer({
            event: 'vehiculo-seleccionado-manual',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.googleTagManagerService.pushBookingActionOnDataLayer({
            event_name: 'vehiculo-seleccionado-manual',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.openFeedbackDialog(vehicle);
          this.umcSelectorsReturn.emit({
            vehicle: vehicle,
            type: 'umcSelector',
          });
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  }

  private createPayload(): UmcPayload {
    const umcPayload: UmcPayload = {};
    const make = this.getCarMakeFromId(
      this.getControlValue('makeIdSelect') as string
    );
    const model = !!this.modelYears?.length
      ? this.getCarModelFromId(
          this.getControlValue('modelYearIdSelect') as string
        )
      : this.getCarModelFromId(this.getControlValue('modelIdSelect') as string);
    const propulsionCode = this.getControlValue('propulsionSelect') as string;
    umcPayload.makeCode = make.make_code;
    umcPayload.makeDescription = make.make_description;
    umcPayload.modelDescription = model.model_description;
    umcPayload.umc = model.umc;
    umcPayload.propulsion_code = propulsionCode;

    return umcPayload;
  }

  private setVehicleUmc = (vehicle_id: number, payload: UmcPayload): void => {
    this.vehicleService
      .setVehicleUmc(vehicle_id, payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          if (this.modifiedVehicle) {
            this.umcSelectorsReturn.emit({
              vehicle: vehicle,
              type: 'umcSelector',
            });
          } else {
            this.googleTagManagerService.pushOnDataLayer({
              event: 'vehiculo-seleccionado-manual',
              'vehiculo-marca': vehicle?.vehicle_information?.make_description,
              'vehiculo-modelo':
                vehicle?.vehicle_information?.model_description,
            });
            this.googleTagManagerService.pushBookingActionOnDataLayer({
              event_name: 'vehiculo-seleccionado-manual',
              'vehiculo-marca': vehicle?.vehicle_information?.make_description,
              'vehiculo-modelo':
                vehicle?.vehicle_information?.model_description,
            });
            this.umcSelectorsReturn.emit({
              vehicle: vehicle,
              type: 'umcSelector',
            });
          }
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private createNewVehicleWithUmc = (payload: UmcPayload): void => {
    payload.plate = null;
    if (this.clientId) {
      payload.client_id = this.clientId;
    }
    this.vehicleService
      .createVehicle(payload)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.googleTagManagerService.pushOnDataLayer({
            event: 'vehiculo-seleccionado-manual',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.googleTagManagerService.pushBookingActionOnDataLayer({
            event_name: 'vehiculo-seleccionado-manual',
            'vehiculo-marca': vehicle?.vehicle_information?.make_description,
            'vehiculo-modelo': vehicle?.vehicle_information?.model_description,
          });
          this.openFeedbackDialog(vehicle);
          this.umcSelectorsReturn.emit({
            vehicle: vehicle,
            type: 'umcSelector',
          });
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private openFeedbackDialog(vehicle: Vehicle): void {
    return;
    const plate = vehicle?.plate ? vehicle.plate : '';
    const model =
      vehicle?.vehicle_information &&
      vehicle?.vehicle_information.make_description
        ? vehicle?.vehicle_information.make_description +
          ' ' +
          vehicle?.vehicle_information.model_description
        : '';
    this.dialog.open(FeedbackDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        icon: 'assets/images/icons/ok-icon.svg',
        title: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleTitle'
        ) as string,
        body: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleBody',
          { plate: plate, model: model }
        ) as string,
      },
    });
  }

  onClickNotFoundCarOption = (): void => {
    this.notFoundCarOptionReturn.emit(true);
  };

  hasErrors(fieldName: string): boolean {
    const control = this.vehicleDataForm.get(fieldName);

    return !!control?.invalid && !!control.touched;
  }

  isValidField(fieldName: string): boolean {
    const control = this.vehicleDataForm.get(fieldName);

    return !!control?.valid;
  }

  private getCarMakeFromId(Id: string): CarMake {
    return this.makes.find((make: CarMake) => make.id === Id);
  }

  private getCarModelFromId(Id: string): CarModel {
    return this.models.find((model: CarModel) => model.id === Id);
  }

  private getControl(controlName: string): AbstractControl {
    return this.umcForm.get(controlName);
  }

  private getControlValue(controlName: string): unknown {
    return this.getControl(controlName).value as unknown;
  }

  get isButtonDisabled(): boolean {
    return (
      this.umcForm?.invalid || this.loadingModels || this.loadingPropulsion
    );
  }

  get isButtonDataDisabled(): boolean {
    return this.vehicleDataForm?.invalid;
  }
}
